import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Coffee, Sparkles, Calendar } from "lucide-react";

const BRUNCH_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/66f23618-666b-435c-8e09-4c16f41965ca/WMWE+Brunch+2023.png";
const GATHERING_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/e10d0153-5fbb-484e-b9da-2908a1ba4b5c/wmwe+pic+me+and+jania.jpg";

const whoFor = [
    "I'm craving deeper connection.",
    "I desire friendships rooted in faith.",
    "I want a safe, sacred space to grow.",
    "I'm tired of surface-level conversations.",
    "I long for community that feels like home.",
    "I want to learn, grow, worship, and commune with other women.",
    "I'm ready to be supported, strengthened, and seen.",
];

const structure = [
    { icon: Calendar, title: "Monthly Fellowship Gathering", body: "A guided, heart-centered gathering with a theme, message, reflection, and connection moment." },
    { icon: Users, title: "Connection Circles", body: "Small, intimate groups for support, prayer, accountability, and real conversation." },
    { icon: Heart, title: "The Community Hub", body: "A private digital space for encouragement, check-ins, prayer requests, and shared wisdom." },
    { icon: Coffee, title: "Quarterly Experiences", body: "Brunches, dinners, retreats, and service gatherings that deepen the bond." },
    { icon: Sparkles, title: "Annual Gathering", body: "A signature, in-person experience that anchors the year." },
];

const whatWmweIs = [
    "reconnect with themselves",
    "build meaningful, life-giving relationships",
    "grow in faith and purpose",
    "share experiences openly",
    "receive support, encouragement, and clarity",
    "experience connection that feels aligned, not forced",
];

export default function WMWEHome() {
    return (
        <main data-testid="wmwe-home" className="bg-sanct-sand text-sanct-charcoal">
            {/* SECTION 1 — PRAYERFUL HERO */}
            <section className="py-32 md:py-48 bg-sanct-cream relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(ellipse at center, transparent 0%, rgba(215,192,179,0.25) 100%)"
                }} />
                <div className="relative max-w-[820px] mx-auto px-6 md:px-10 text-center">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-10">Before you read anything else…</p>
                    <h1 className="font-serif text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                        Breathe.
                        <span className="block italic text-sanct-rose mt-2">You&apos;re here on purpose.</span>
                    </h1>

                    <div className="mt-20 md:mt-24 max-w-[620px] mx-auto">
                        <p className="font-sans text-[11px] uppercase tracking-[0.35em] text-sanct-cocoa/60 mb-8">A Prayer for the Woman Arriving Here</p>
                        <p className="font-serif italic text-xl md:text-2xl leading-[1.8] text-sanct-cocoa">
                            God, let the woman reading this feel Your presence.<br />
                            Let her feel seen, held, and gently guided.<br />
                            Let this be a place where her heart can rest,<br />
                            where her spirit can rise,<br />
                            and where her connections are divinely aligned.<br /><br />
                            Prepare her for the community she&apos;s been praying for —<br />
                            a fellowship that strengthens her,<br />
                            a sisterhood that surrounds her,<br />
                            and a sanctuary that reminds her she is never alone.
                        </p>
                        <p className="font-serif italic text-2xl text-sanct-rose mt-10">Amen.</p>
                    </div>
                </div>
            </section>

            {/* SECTION 2 — SACRED INVITATION (two-column) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative">
                            <img src={BRUNCH_IMG} alt="WMWE Brunch gathering" className="w-full aspect-[4/5] object-cover rounded-sm" />
                            <div className="absolute inset-0 rounded-sm ring-1 ring-sanct-clay/40" />
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Sacred Invitation</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                            Welcome to <span className="italic text-sanct-rose">We Meet We Eat<sup className="text-[0.4em] not-italic ml-0.5 align-super opacity-80">&reg;</sup></span>
                        </h2>
                        <p className="font-sans text-base md:text-lg text-sanct-charcoal/85 mt-8 leading-[1.8]">
                            A fellowship for women who desire deeper connection with God, with themselves, and with each other.
                        </p>
                        <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa mt-6 leading-[1.5]">
                            A sanctuary for your soul. A sisterhood for your journey. A place where you can exhale, be poured into, and be surrounded by women who truly see you.
                        </p>
                        <div className="mt-8 pt-8 border-t border-sanct-rose/40 space-y-2 font-serif italic text-lg md:text-xl text-sanct-charcoal/75">
                            <p>This is not an event.</p>
                            <p>This is not a club.</p>
                            <p>This is not a moment.</p>
                            <p className="not-italic font-serif text-xl md:text-2xl text-sanct-rose pt-3">This is a fellowship. A home. A spiritual community.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 3 — WHAT WMWE IS (soft clay bg, rounded corners) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1100px] mx-auto px-6 md:px-10">
                    <div className="bg-sanct-clay rounded-[32px] p-10 md:p-16">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-cocoa/70 mb-6">What WMWE Is</p>
                        <h2 className="font-serif text-3xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight mb-10">
                            A God&#8209;led fellowship where women come together to:
                        </h2>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 font-serif italic text-xl md:text-2xl text-sanct-cocoa/90">
                            {whatWmweIs.map((line) => (
                                <li key={line} className="flex gap-3">
                                    <span className="text-sanct-rose not-italic">&#10038;</span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="font-serif text-xl md:text-2xl italic text-sanct-cocoa mt-14 leading-[1.5] pt-10 border-t border-sanct-cocoa/20">
                            It&apos;s a space where you don&apos;t have to perform. You don&apos;t have to pretend. You don&apos;t have to carry everything alone.
                        </p>
                        <p className="font-serif text-xl md:text-2xl text-sanct-rose italic mt-4">
                            Come as you are — and be met with love, wisdom, and community.
                        </p>
                    </div>
                </div>
            </section>

            {/* SECTION 4 — WHO WMWE IS FOR (grid of cards, rose hover) */}
            <section className="py-24 md:py-32 bg-sanct-cream">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <div className="text-center max-w-[640px] mx-auto mb-16">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Who WMWE Is For</p>
                        <h2 className="font-serif text-3xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight">
                            This fellowship is for the woman who…
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {whoFor.map((line) => (
                            <div
                                key={line}
                                className="group bg-sanct-sand rounded-[20px] p-8 md:p-10 border border-sanct-clay/50 hover:bg-sanct-rose hover:border-sanct-rose transition-colors duration-500 cursor-default"
                            >
                                <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa group-hover:text-sanct-cream leading-[1.35] transition-colors">
                                    &ldquo;{line}&rdquo;
                                </p>
                            </div>
                        ))}
                    </div>
                    <p className="font-serif text-2xl md:text-3xl italic text-sanct-rose mt-16 text-center">
                        If your heart whispered &ldquo;yes,&rdquo; you&apos;re in the right place.
                    </p>
                </div>
            </section>

            {/* SECTION 5 — FELLOWSHIP STRUCTURE (horizontal icons) */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="mb-16 max-w-[800px]">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">The Fellowship Structure</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight">
                            How we gather. How we grow.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
                        {structure.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.title} className="relative">
                                    <div className="w-14 h-14 rounded-full bg-sanct-clay flex items-center justify-center mb-5">
                                        <Icon size={22} className="text-sanct-cocoa" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-serif text-xl md:text-2xl text-sanct-cocoa leading-tight mb-3">{s.title}</h3>
                                    <p className="font-sans text-sm text-sanct-charcoal/75 leading-[1.7]">{s.body}</p>
                                    {i < structure.length - 1 && (
                                        <div className="hidden lg:block absolute top-7 -right-4 w-8 h-px bg-sanct-rose/50" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 6 — MEMBERSHIP OVERVIEW (two-column: list + image) */}
            <section className="py-24 md:py-32 bg-sanct-clay/40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Membership Overview</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight mb-10">
                            What you receive as a member.
                        </h2>
                        <ul className="space-y-4 font-serif italic text-lg md:text-xl text-sanct-cocoa/85">
                            {[
                                "Monthly fellowship gatherings",
                                "Placement in a Connection Circle",
                                "Access to the private community hub",
                                "Digital reflections + guided prompts",
                                "Early access to WMWE events",
                                "Member pricing for retreats",
                                "Priority access to the Fellowship Cohort",
                                "Exclusive content from Shana",
                            ].map((line) => (
                                <li key={line} className="flex gap-4 items-start border-b border-sanct-rose/30 pb-4">
                                    <span className="text-sanct-rose not-italic text-lg">&#10038;</span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <img src={GATHERING_IMG} alt="WMWE gathering" className="w-full aspect-[4/5] object-cover rounded-sm" />
                    </div>
                </div>
            </section>

            {/* SECTION 7 — SACRED CTA */}
            <section className="py-32 md:py-40 bg-sanct-cream text-center">
                <div className="max-w-[820px] mx-auto px-6 md:px-10">
                    <p className="font-serif italic text-2xl md:text-3xl text-sanct-cocoa leading-[1.5] mb-4">
                        If your spirit is nudging you… trust that.
                    </p>
                    <p className="font-serif italic text-xl md:text-2xl text-sanct-charcoal/80 leading-[1.6]">
                        You&apos;re not here by accident. You&apos;re here because God is drawing you into community, connection, and growth.
                    </p>
                    <div className="mt-14 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/wmwe/fellowship#waitlist"
                            data-testid="wmwe-home-join-btn"
                            className="inline-flex items-center gap-3 px-9 py-5 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors duration-300"
                        >
                            Join the Fellowship <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/wmwe/gatherings"
                            className="inline-flex items-center gap-3 px-9 py-5 border border-sanct-cocoa text-sanct-cocoa text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa hover:text-sanct-cream transition-colors duration-300"
                        >
                            Get Notified About Gatherings
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
